<template>
  <FlotoForm
    ref="form"
    :show-notification="false"
    layout="vertical"
    @submit="handleAddTag"
  >
    <MValidationProvider
      ref="emailValidator"
      :rules="validationRules"
      :name="$tc('email')"
      mode="lazy"
    >
      <MFormItem
        slot-scope="{ errors, invalid, valid, validated }"
        class="mb-0"
        :error="errors[0]"
        :validate-status="validated ? (valid ? 'success' : 'error') : undefined"
      >
        <div class="flex flex-wrap items-center email-input-container">
          <template v-if="!ccEmail"> To : </template>
          <template v-else> Cc : </template>
          <template v-for="(tag, index) in tags">
            <MTag
              :key="tag"
              :ref="`tagRef-${index}`"
              :variant="variant"
              class="flex items-center mx-1 my-1 email-tag"
              :aria-label="`${$t('remove_tag', { tag: tag || $tc('tag') })}`"
              :rounded="true"
              :closable="!disabled"
              :confirmable="confirmBeforeRemove"
              :ok-text="okText"
              :cancel-text="cancelText"
              @close="handleRemoveTag(tag)"
            >
              <!-- <FlotoAvatarName
              :size="size"
              :avatar="getEmailName(tag)"
              :name="tag"
              show-tooltip
            /> -->
              {{ tag }}
            </MTag>
          </template>
          <slot name="prefix-text"> </slot>

          <div class="flex-grow email-input-wrapper">
            <FlotoRequesterEmail
              :key="resetKey"
              v-model="tagValue"
              is-email
              :show-hint="false"
              auto-focus
              size="small"
              :advanced-searchable="false"
              :placeholder="placeholder || ''"
              v-bind="$attrs"
              @pressEnter="validateAndAddTag"
              @keydown.tab.prevent="validateAndAddTag"
              @keydown.enter.prevent="validateAndAddTag"
              @blur="validateAndAddTag"
              @select="handleSelect"
            />
          </div>
          <span
            v-if="!ccEmail"
            class="text-neutral-light cursor-pointer"
            @click="toggleCc"
          >
            cc
          </span>
        </div>
      </MFormItem>
    </MValidationProvider>
    <template v-slot:submit><span /></template>
  </FlotoForm>
</template>

<script>
export default {
  name: 'FlotoEmailPicker',
  model: {
    event: 'change',
  },
  props: {
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: undefined },
    variant: { type: String, default: 'neutral-lighter' },
    confirmBeforeRemove: { type: Boolean, default: false },
    okText: {
      type: String,
      default() {
        return this.$tc('yes')
      },
    },
    cancelText: {
      type: String,
      default() {
        return this.$tc('cancel')
      },
    },
    ccEmail: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'small',
    },
    rules: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      tagValue: '',
      resetKey: 0,
      selectedRequester: null,
    }
  },
  computed: {
    tags() {
      return this.value || []
    },
    validationRules() {
      return this.rules || { email: true }
    },
  },
  methods: {
    toggleCc() {
      this.showCc = !this.showCc
      this.$emit('toggle-cc', this.showCc)
    },
    getEmailName(email) {
      return email.split('@')[0]
    },
    resetInput() {
      this.tagValue = ''
      this.$nextTick(() => {
        this.resetKey += 1
      })
    },

    handleSelect(value) {
      if (!value) return

      if (!this.isDuplicate(value)) {
        const updatedValue = [...this.tags, value]
        this.$emit('change', updatedValue)
        this.resetInput()
      }
    },

    async validateAndAddTag() {
      if (!this.tagValue) return

      if (this.isDuplicate(this.tagValue)) {
        this.resetInput()
        return
      }

      try {
        const validator = this.$refs.emailValidator
        const { valid } = await validator.validate(this.tagValue)

        if (valid === true) {
          const updatedValue = [...this.tags, this.tagValue]
          this.$emit('change', updatedValue)
          this.resetInput()
        }
      } catch (error) {}
    },

    isDuplicate(tag) {
      return this.tags.includes(tag)
    },

    handleRemoveTag(tag) {
      const updatedValue = this.tags.filter((t) => t !== tag)
      this.$emit('change', updatedValue)
    },
  },
}
</script>

<style lang="less" scoped>
// stylelint-disable-next-line
.mb-0 {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

.email-input-container {
  min-height: 32px;
  padding: 2px 8px;
  background: var(--page-background-color);
  border-bottom: 1px solid var(--border-color);
}

.email-input-wrapper {
  :deep(.ant-form-item) {
    margin-bottom: 0;
  }

  :deep(.ant-input) {
    height: 28px;
    padding: 4px;
    border: none;

    &:focus {
      box-shadow: none;
    }
  }
}

.email-tag {
  height: 24px;
  padding: 0 8px;
  margin: 2px;
  line-height: 24px;
  background-color: var(--neutral-lighter);
  border-radius: 16px;
}

:deep(.has-error) {
  .ant-form-item-explain {
    min-height: 20px;
    padding-top: 2px;
    font-size: 12px;
    color: var(--error-color, #ff4d4f);
  }

  .ant-input {
    border-color: var(--error-color, #ff4d4f);

    &:focus {
      border-color: var(--error-color, #ff4d4f);
      box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
    }
  }
}

.removable-tag:focus-visible {
  svg[data-icon='times'] {
    border-radius: 50%;
    outline: 1px solid var(--primary);
    outline-offset: 1px;
    fill: var(--primary);

    path {
      fill: var(--primary);
    }
  }
}
</style>
